import { useEffect, useRef, useState } from 'preact/hooks';
import { route } from 'preact-router';

import AppLoader from '@/components/AppLoader';
import { useSelector } from '@/redux/helper';
import { isEducatorsPath, toEducatorsURL } from '@/shared/educatorsApp';

import { isPopupReturn, PAID_MESSAGE, reportToOpenerAndClose, WEBHOOK_HEAD_START_MS, withReport } from './popup';
import trackPurchase from './purchaseAnalytics';

/**
 * Where Stripe sends a successful checkout.
 *
 * Three shapes. A normal checkout continues to `/welcome`, which fires the purchase analytics and
 * moves on. A Speedy Flow checkout is running in a tab whose opener is still sitting behind the
 * modal: it never reaches `/welcome`, so it fires the same analytics itself, tells the opener, and
 * closes. A Speedy Flow checkout whose opener is on the *educators* app cannot be heard from this
 * origin at all, so it hops there first and reports from the other side.
 */
const CheckoutCompleted = () => {
  const [isPopup, setIsPopup] = useState(false);
  const educatorsAppURL = useSelector((state) => state.remoteConfig?.educatorsAppURL);
  /**
   * `educatorsAppURL` arrives with the remote config, which lands a beat after mount, so this
   * effect can run more than once. Both things it does are once-only — the analytics would
   * otherwise double-count a single purchase, and the tab reports and closes — so each is claimed
   * by the first pass that gets to it. They are claimed separately because only the second waits.
   */
  const trackedRef = useRef(false);
  const reportedRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const passType = params.get('passType');
    const price = params.get('price');
    const redirect = params.get('co-redirect');

    const welcomePath = `/welcome?plan=${passType}&price=${price}${
      redirect ? `&co-redirect=${encodeURIComponent(redirect)}` : ''
    }`;

    /**
     * The marker says how this tab was *meant* to come back; the opener says how it actually can.
     *
     * They disagree whenever the popup was blocked: the flow then sends this same tab to Stripe,
     * and the return path it built earlier still says `zlpopup=1`. Trusting the marker alone left
     * such a visitor on the loader for good — there was nobody to post to, and `window.close()`
     * does nothing to a tab a script did not open.
     *
     * Deciding it here rather than at the report is what keeps the analytics honest: with no
     * opener this is an ordinary full-page checkout, so it goes to `/welcome` and lets `/welcome`
     * count the purchase, exactly once.
     */
    const opener = window.opener as Window | null;
    if (isPopupReturn(window.location.search) && !!opener && !opener.closed) {
      setIsPopup(true);

      // First, and whatever happens below: these do not depend on where the tab reports to, and a
      // purchase that cannot be reported is still a purchase that has to be counted.
      if (!trackedRef.current) {
        trackedRef.current = true;
        trackPurchase({ passType, price });
      }

      // An educators checkout. Its opener is on the other origin, so posting from here would be
      // shouting into a tab that is not listening (see `shared/educatorsApp`). Hop, carrying the
      // outcome, and let the page that lands there say it.
      const decoded = redirect ? decodeURIComponent(redirect) : undefined;
      const educators = isEducatorsPath(decoded);

      // The hop is the one thing here that needs the remote config, so it is the one thing worth
      // waiting for. Reporting to the opener instead would be reporting to a tab on another origin
      // that cannot hear it — a silent dead end — so do nothing and let the effect run again when
      // the URL lands.
      if (educators && !educatorsAppURL) {
        return;
      }

      if (reportedRef.current) {
        return;
      }
      reportedRef.current = true;

      const hop = educators ? toEducatorsURL(educatorsAppURL, decoded as string) : undefined;
      if (hop) {
        // The same pause a same-origin popup takes: the beat gives the webhook a head start, and it
        // also gives the beacons fired above time to leave before the page is torn down. The
        // educators app's own boot is further head start on top, which is only ever a help.
        setTimeout(() => window.location.replace(withReport(hop, PAID_MESSAGE)), WEBHOOK_HEAD_START_MS);
        return;
      }

      // The opener will poll for the entitlement regardless; the pause just gives the webhook a
      // head start so the common case resolves on the first check. Should the opener be gone by
      // the time the pause is up, land somewhere real rather than on this loader — but not on
      // `/welcome`, which would count a purchase already counted above.
      reportToOpenerAndClose(PAID_MESSAGE, WEBHOOK_HEAD_START_MS, () =>
        route(decoded?.startsWith('/') && !decoded.startsWith('//') ? decoded : '/home', true),
      );
      return;
    }

    route(welcomePath, true);
  }, [educatorsAppURL]);

  if (isPopup) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-2 p-6 text-center">
        <h1 className="preflight preflight-h1 text-xl font-bold text-[#162B1B]">Payment received</h1>
        <p className="preflight preflight-p text-sm text-grey-3">Taking you back…</p>
      </div>
    );
  }

  return <AppLoader />;
};

export default CheckoutCompleted;
